package com.klantenstop.web.auth

import com.materialkobweb.utils.Logger
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

enum class AuthRole {
    Admin,
    Moderator,
    Consumer
}

data class AuthState(
    val authenticated: Boolean,
    val role: AuthRole = AuthRole.Consumer,
    val userId: String? = null
)

interface AuthGateway {
    suspend fun login(username: String, password: String)
    suspend fun checkAuthStatus(): AuthState
    suspend fun refreshToken()
    suspend fun logout()
}

object AuthModule {
    private val _authStatus = MutableStateFlow<AuthState?>(null)
    val authStatus: StateFlow<AuthState?> = _authStatus.asStateFlow()

    private var gateway: AuthGateway? = null

    fun init(gateway: AuthGateway) {
        this.gateway = gateway
    }

    suspend fun refreshAuthStatus(): AuthState {
        val currentGateway = requireNotNull(gateway) {
            "AuthModule gateway is not configured."
        }
        return currentGateway.checkAuthStatus().also { _authStatus.value = it }
    }

    suspend fun login(username: String, password: String): AuthState {
        val currentGateway = requireNotNull(gateway) {
            "AuthModule gateway is not configured."
        }
        currentGateway.login(username, password)
        return refreshAuthStatus()
    }

    suspend fun refreshSession(): AuthState {
        val currentGateway = requireNotNull(gateway) {
            "AuthModule gateway is not configured."
        }
        currentGateway.refreshToken()
        return refreshAuthStatus()
    }

    suspend fun logout() {
        val currentGateway = requireNotNull(gateway) {
            "AuthModule gateway is not configured."
        }
        try {
            currentGateway.logout()
        } catch (e: Throwable) {
            Logger.warn("AuthModule", "Logout request failed; clearing local auth state anyway.", e)
        } finally {
            // regardless of the `logout` network call's response, we need to clear the local auth state
            setUnauthenticated()
        }
    }

    fun setUnauthenticated() {
        _authStatus.value = AuthState(authenticated = false)
    }

    fun isAdmin(): Boolean = _authStatus.value?.role == AuthRole.Admin

    fun isModerator(): Boolean = _authStatus.value?.role == AuthRole.Moderator
}
